// apps/console/src/services/auth/keycloak-client.ts
//
// Plan 4 Tasks 5 + 6 — Keycloak client. Direct-REST client mirroring the
// cognito-client.ts surface for the SPA's existing auth UX. The 5 token-flow
// methods (signIn / respondToMfaChallenge / refreshToken / signOut /
// getAccessToken) go straight to Keycloak's /protocol/openid-connect/*
// endpoints. The remaining 7 methods (forgot-password, change-password,
// MFA enroll/verify/reset, confirmForgotPassword, respondToNewPasswordChallenge)
// land below — 5 route through the Task 4 backend wrapper endpoints; 2 throw
// with provider-specific guidance for flows that don't exist in Keycloak
// (code-based reset, NEW_PASSWORD_REQUIRED). There is no sign-up surface at all:
// users are provisioned server-side on both providers.
//
// Token storage reuses the existing localStorage keys (`gisify_access_token`
// / `gisify_refresh_token`) so the cognito client's getAccessToken() /
// clearStoredTokens() semantics carry over unchanged when the factory in
// Task 7 picks one of these at boot.
//
// MFA heuristic: Keycloak direct grant returns 401 + error_description
// containing 'invalid' to signal both wrong-password AND MFA-required. We
// replicate the backend KeycloakAuthProvider.signIn heuristic (see
// libs/providers/src/auth/keycloak-provider.ts:92-113) — same comment block
// applies. When the backend's heuristic moves or evolves, this frontend
// twin must be reviewed too.

import { getApiUrl } from '../../config/env'
import { getAuthConfig } from './config'
import { isPasswordUpdateRequired, parseGrantError, passwordUpdateUrl } from './keycloak-password-update'
import type { SignInResult } from './types'

const REFRESH_TOKEN_KEY = 'gisify_refresh_token'
const ACCESS_TOKEN_KEY = 'gisify_access_token'

function authority(): string {
  const cfg = getAuthConfig()

  if (cfg.provider !== 'keycloak') throw new Error('keycloak-client.ts called under non-keycloak provider')

  return cfg.keycloak.authority.replace(/\/$/, '')
}

function clientId(): string {
  const cfg = getAuthConfig()

  if (cfg.provider !== 'keycloak') throw new Error('keycloak-client.ts called under non-keycloak provider')

  return cfg.keycloak.clientId
}

function tokenEndpoint(): string {
  return `${authority()}/protocol/openid-connect/token`
}

function logoutEndpoint(): string {
  return `${authority()}/protocol/openid-connect/logout`
}

function storeTokens(body: { access_token: string; refresh_token?: string }): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, body.access_token)
  if (body.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, body.refresh_token)
}

function clearStoredTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: clientId(),
    username: email,
    password,
    scope: 'openid profile email',
  })
  const res = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  })

  if (res.ok) {
    const json = (await res.json()) as { access_token: string; refresh_token: string }

    storeTokens(json)

    return { kind: 'authenticated', accessToken: json.access_token }
  }

  if (res.status === 400 || res.status === 401) {
    const text = await res.text()
    const json = parseGrantError(text)

    // A pending required action (a temporary password): only Keycloak's hosted pages can
    // complete it (keycloak-password-update.ts). Checked before the MFA heuristic below.
    if (isPasswordUpdateRequired(json)) {
      const actionUrl = passwordUpdateUrl({ authority: authority(), email })

      return { kind: 'password-update-required', actionUrl }
    }

    if (res.status === 400) throw new Error(`signIn: 400 ${text}`)

    if (typeof json.error_description === 'string' && json.error_description.toLowerCase().includes('invalid')) {
      // HEURISTIC: Keycloak's direct-access-grants flow does NOT return a clean
      // "MFA required" signal the way Cognito's SOFTWARE_TOKEN_MFA challenge
      // does. When a user has a TOTP credential and the request omits the
      // `totp` form field, Keycloak responds with `401 invalid_grant` carrying
      // an `error_description` like "Invalid user credentials" or "Account is
      // not fully set up". We treat any 401 whose `error_description` includes
      // 'invalid' as the MFA challenge boundary and synthesize a session token
      // (base64 of {email, password}) that respondToMfaChallenge re-submits
      // along with the TOTP code.
      //
      // This conflates "wrong password" with "MFA required" — both surface as
      // `mfa-required`. The downstream UX is acceptable (the OTP prompt
      // rejects a wrong password on the second attempt with a clear error),
      // but contributors should know this is a heuristic, not a missed
      // Keycloak feature.
      //
      // The base64 wrapping of {email, password} is NOT for security — it's
      // just so the session string can travel through React state / URL
      // params without escaping. Do not assume the session is encrypted.
      //
      // CROSS-REFERENCE: This is the FRONTEND twin of the backend
      // KeycloakAuthProvider.signIn heuristic at
      // libs/providers/src/auth/keycloak-provider.ts:92-113. The two MUST
      // stay in sync — if the backend's heuristic moves or evolves
      // (e.g. switches to a different 401 detection, adopts a Keycloak-side
      // MFA challenge token), update this block at the same time.
      const session = btoa(JSON.stringify({ email, password }))

      return { kind: 'mfa-required', session, challengeName: 'SOFTWARE_TOKEN_MFA' }
    }

    throw new Error(`signIn: ${res.status} ${json.error_description ?? 'unauthorized'}`)
  }

  throw new Error(`signIn: ${res.status} ${await res.text()}`)
}

export async function respondToMfaChallenge(_email: string, code: string, session: string): Promise<string> {
  const decoded = JSON.parse(atob(session)) as { email: string; password: string }
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: clientId(),
    username: decoded.email,
    password: decoded.password,
    totp: code,
    scope: 'openid profile email',
  })
  const res = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  })

  if (!res.ok) throw new Error(`respondToMfaChallenge: ${res.status} ${await res.text()}`)

  const json = (await res.json()) as { access_token: string; refresh_token: string }

  storeTokens(json)

  return json.access_token
}

/**
 * True only when Keycloak DEFINITIVELY rejected the refresh token — revoked,
 * expired, or its session/user is gone. The OAuth2 token endpoint signals that
 * with a 400/401 carrying `invalid_grant`; a 5xx, a 429 throttle or a proxy
 * error means the grant was never judged at all.
 *
 * Deliberately an allow-list, matching cognito-client's
 * `isPermanentRefreshRejection`: an unrecognised failure is treated as
 * recoverable, because the cost of wrongly keeping a dead refresh token (one
 * extra failed round) is far lower than the cost of wrongly destroying a live
 * one (a forced re-login).
 *
 * CROSS-REFERENCE: this is the Keycloak twin of the Cognito classifier in
 * cognito-client.ts. services/api/client.ts reads "did a refresh token survive
 * the failed refresh?" as its single, PROVIDER-AGNOSTIC signal for "may this
 * session be torn down?" — so both clients must classify the same way or
 * on-prem gets a session teardown where cloud gets a retry.
 */
async function isPermanentRefreshRejection(res: Response): Promise<boolean> {
  if (res.status !== 400 && res.status !== 401) return false

  const json = (await res.json().catch(() => ({}))) as { error?: string; error_description?: string }

  return /invalid_grant/i.test(`${json.error ?? ''} ${json.error_description ?? ''}`)
}

/**
 * Silent re-auth from the stored refresh token. Returns the new access token,
 * or `null` when the round failed.
 *
 * INVARIANT: the stored tokens are dropped ONLY on a definitive rejection of
 * the refresh token (see `isPermanentRefreshRejection`). A bare `if (!res.ok)
 * clearStoredTokens()` used to wipe `gisify_refresh_token` on ANY non-200 — so
 * an on-prem Keycloak rolling behind Traefik and answering 503 for a few
 * seconds logged out every user whose token happened to expire in that window,
 * even though their refresh token was perfectly valid.
 *
 * We resolve to `null` rather than throwing on a transient failure for the same
 * reason: `useAuth`'s periodic refresh treats a throw as "log the user out",
 * which is exactly the outcome this guard exists to avoid. Callers that need to
 * tell the two apart read whether a refresh token survived (see
 * `services/api/client.ts`).
 */
export async function refreshToken(): Promise<string | null> {
  const stored = localStorage.getItem(REFRESH_TOKEN_KEY)

  if (!stored) return null

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId(),
    refresh_token: stored,
  })
  // Resolved OUTSIDE the try below so a misconfigured provider still throws
  // loudly instead of being swallowed as a transport blip.
  const endpoint = tokenEndpoint()
  let res: Response

  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    })
  } catch {
    // Offline / DNS / TLS failure — Keycloak never got to reject anything, so
    // the refresh material must survive for the next attempt.
    return null
  }

  if (!res.ok) {
    if (await isPermanentRefreshRejection(res)) clearStoredTokens()

    return null
  }

  const json = (await res.json()) as { access_token: string; refresh_token: string }

  storeTokens(json)

  return json.access_token
}

export async function signOut(): Promise<void> {
  const refresh = localStorage.getItem(REFRESH_TOKEN_KEY)

  if (refresh) {
    const body = new URLSearchParams({ client_id: clientId(), refresh_token: refresh })

    await fetch(logoutEndpoint(), {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    }).catch(() => {})
  }

  clearStoredTokens()
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export { clearStoredTokens }

// --- Plan 4 Task 6: backend-wrapper helpers + 7 remaining IFrontendAuthClient
//     methods (5 wrappers + 2 Cognito-only throws). The wrappers POST to the
//     Task 4 endpoints minted alongside the KeycloakAuthProvider; the throws
//     surface provider-specific guidance for flows Keycloak doesn't speak
//     (code-based reset, NEW_PASSWORD_REQUIRED).

type BackendError = Error & { code?: string; statusCode?: number }

/**
 * Authed POST helper for the Task 4 backend endpoints. Reads the access token
 * stored by signIn / respondToMfaChallenge / refreshToken and attaches it as
 * a Bearer header. Throws with a `code: 'NotAuthorizedException'` on 401 to
 * match the existing Cognito-client convention so callers don't need to
 * branch on provider for unauthenticated handling.
 */
async function postAuthed<T = unknown>(path: string, body: unknown): Promise<T> {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)

  if (!accessToken) throw new Error(`${path}: no access token in storage — sign in first`)

  const res = await fetch(getApiUrl(path), {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { message?: string }
    const err: BackendError = new Error(`${path}: ${res.status} ${errBody.message ?? res.statusText}`)

    err.statusCode = res.status
    if (res.status === 401) err.code = 'NotAuthorizedException'

    throw err
  }

  // Tolerate 204 No Content by returning an empty object cast — Task 4 handlers
  // all return JSON today, but the change-password / mfa-reset shapes are
  // intentionally minimal and could shrink to 204 in future without breaking
  // this helper.
  if (res.status === 204) return {} as T

  return (await res.json()) as T
}

/**
 * Public POST helper — no Authorization header. Used for forgot-password which
 * MUST be reachable while the user is signed out.
 */
async function postPublic<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(getApiUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { message?: string }

    throw new Error(`${path}: ${res.status} ${errBody.message ?? res.statusText}`)
  }

  if (res.status === 204) return {} as T

  return (await res.json()) as T
}

// --- 5 backend-wrapper methods ----------------------------------------------

export async function forgotPassword(email: string): Promise<void> {
  await postPublic('/auth/forgot-password', { email })
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await postAuthed('/users/me/password', { currentPassword, newPassword })
}

export async function associateSoftwareToken(): Promise<string> {
  const result = await postAuthed<{ secret: string }>('/users/me/mfa/enroll', {})

  return result.secret
}

export async function verifySoftwareToken(userCode: string): Promise<void> {
  // Keycloak: the TOTP credential is created at enroll-time via the
  // `/users/me/mfa/enroll` PUT-on-user-with-credentials shape (Spike 1 finding
  // 6) and goes live immediately. The frontend doesn't need a separate verify
  // round-trip on Keycloak — once the user has the secret in their
  // authenticator, signing in with TOTP works.
  //
  // Kept as a no-op so consumers (useAuth, MfaSetupModal) can call this
  // uniformly across providers without branching. `void` marks the param
  // intentionally-unused for eslint.
  void userCode
}

export async function setUserMFAPreference(enabled: boolean): Promise<void> {
  if (enabled) {
    // Keycloak: enabling MFA happens implicitly at enroll-time via the
    // `/users/me/mfa/enroll` call above (Spike 1 finding 6). There's no
    // separate enable step. No-op.
    return
  }

  // Disable: route through the backend's reset endpoint which delegates to
  // authProvider.resetMfa (Cognito: AdminSetUserMFAPreference disable;
  // Keycloak: DELETE the OTP credential on the user).
  await postAuthed('/users/me/mfa/reset', {})
}

// --- 2 Cognito-only flows (throws with provider-specific guidance) ----------

// `void <param>` lines on each throw body silence eslint's
// @typescript-eslint/no-unused-vars rule without renaming params (the
// IFrontendAuthClient interface fixes the names, so a noop `void` reference
// is the cleanest way to keep the param-list lining up).

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  void email
  void code
  void newPassword
  throw new Error(
    "Keycloak password reset is link-driven, not code-driven. The user clicks the email link to set a new password via Keycloak's hosted page.",
  )
}

export async function respondToNewPasswordChallenge(
  email: string,
  newPassword: string,
  session: string,
  userAttributes: Record<string, string>,
): Promise<string> {
  void email
  void newPassword
  void session
  void userAttributes
  throw new Error(
    "NEW_PASSWORD_REQUIRED is a Cognito challenge flow. Keycloak's required-actions (e.g. UPDATE_PASSWORD) are handled by Keycloak's hosted pages during sign-in.",
  )
}
